import { useCallback, useState } from "react";
import { SharedLoginService } from "../../../api/sharedLogin.service";
import { getDashErrorCode } from "../../FlightAlerts/handler/apiError.helper";

export interface HandOverResult {
  success: boolean;
  returnContextToken?: string;
  errorCode?: string;
}

export interface HandBackResult {
  success: boolean;
  phoneNumber?: string;
  destination?: string;
  errorCode?: string;
}

export interface UseReturnContextResult {
  handOver: (verifiedPhoneToken: string, destination: string) => Promise<HandOverResult>;
  handBack: (returnContextToken: string) => Promise<HandBackResult>;
  isWorking: boolean;
}


export const useReturnContext = (): UseReturnContextResult => {
  const [isWorking, setIsWorking] = useState(false);

  const handOver = useCallback(
    async (verifiedPhoneToken: string, destination: string): Promise<HandOverResult> => {
      setIsWorking(true);
      try {
        const service = new SharedLoginService();
        const response = await service.mintReturnContext(verifiedPhoneToken, destination);
        return { success: true, returnContextToken: response.data.returnContextToken };
      } catch (err) {
        const errorCode = getDashErrorCode(err);
        return errorCode !== undefined ? { success: false, errorCode } : { success: false };
      } finally {
        setIsWorking(false);
      }
    },
    [],
  );

  const handBack = useCallback(async (returnContextToken: string): Promise<HandBackResult> => {
    setIsWorking(true);
    try {
      const service = new SharedLoginService();
      const response = await service.consumeReturnContext(returnContextToken);
      return {
        success: true,
        phoneNumber: response.data.phoneNumber,
        destination: response.data.destination,
      };
    } catch (err) {
      const errorCode = getDashErrorCode(err);
      return errorCode !== undefined ? { success: false, errorCode } : { success: false };
    } finally {
      setIsWorking(false);
    }
  }, []);

  return { handOver, handBack, isWorking };
};
